/**
 * Browser error reporting (docs/09): Sentry is initialised only when the build carries a
 * DSN (`VITE_SENTRY_DSN`, the Terraform output for the environment's web project). Local
 * dev and tests carry none and do nothing. The release is the deployed commit, so an event
 * points at the code that produced it.
 */
import * as Sentry from "@sentry/browser";

export interface ObservabilityEnv {
  dsn?: string;
  environment?: string;
  release?: string;
}

/** Returns true when Sentry was initialised. Exported for the test. */
export function initObservability(env: ObservabilityEnv): boolean {
  if (!env.dsn) return false;
  Sentry.init({
    dsn: env.dsn,
    environment: env.environment ?? "development",
    release: env.release
      ? `truereel-web@${env.release.slice(0, 12)}`
      : undefined,
    // Errors only: no performance tracing, no session replay, nothing typed by the user.
    tracesSampleRate: 0,
    sendDefaultPii: false,
    beforeSend(event) {
      // The API's request id is enough to join an event to the server log; strip URLs of
      // presigned blob links, which carry signatures.
      if (event.request?.url)
        event.request.url = event.request.url.split("?")[0];
      return event;
    },
  });
  return true;
}

export function initFromViteEnv(): boolean {
  return initObservability({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT,
    release: import.meta.env.VITE_RELEASE_SHA,
  });
}
